import { Box } from '@moonpig/launchpad-components'
import React, { FC, useCallback, useMemo, useRef } from 'react'
import { Region } from '@moonpig/web-core-types'
import {
  AddedToBasketEvent,
  PageTypeEnum,
  ProductClickedEvent,
  ProductGrid,
  ProductTileProduct,
  PromoTileData,
} from '@moonpig/web-shared-products'
import Head from 'next/head'
import { useExperiment } from '@moonpig/web-core-eppo/browser'
import { GALLERY_PAGE_SIZE } from '../../constants'
import { LoadMoreType } from '../types'
import { InfiniteScroll } from './InfiniteScroll'
import { useLoadPage } from './useLoadPage'
import { setOffsetAndProductIdInUrl } from './setOffsetAndProductIdInUrl'
import { usePagingOffset } from './usePagingOffset'
import { useSearchStore } from '../../store/SearchStore'
import { useFindLocaleText } from '../../text-localisation'
import { buildSearchTitle } from '../../utils/buildSearchTitle'
import { AgeRangeFilterBanner } from './EntryPointFeatureBanners/AgeRangeFilter'

const setProductPosition = (product: ProductTileProduct) => {
  const productIndex = product.productIndex || 0
  const productPageNumber = Math.floor(productIndex / GALLERY_PAGE_SIZE)
  const productPageOffset = productPageNumber * GALLERY_PAGE_SIZE
  setOffsetAndProductIdInUrl(productPageOffset, product.id)
}

type ProductGridWithPagingProps = {
  totalCount: number
  products: ProductTileProduct[]
  onProductClicked: (clickedEvent: ProductClickedEvent) => void
  onProductFirstClick: (clickedEvent: ProductClickedEvent) => void
  onAddToBasket: (addToBasketEvent: AddedToBasketEvent) => void
  region: Region
  isInternalUser?: boolean
  metaTitle: string
  pageType: PageTypeEnum
  groupCardProject?: string
  promoTile?: PromoTileData
}

export const ProductGridWithPaging: FC<ProductGridWithPagingProps> = ({
  totalCount,
  products,
  onProductClicked,
  onProductFirstClick,
  onAddToBasket,
  region,
  isInternalUser = false,
  metaTitle,
  pageType,
  groupCardProject,
  promoTile,
}) => {
  const localiseText = useFindLocaleText()
  const { departments } = useSearchStore(store => store.searchContext)
  const loadMoreProducts = useSearchStore(store => store.loadMoreProducts)

  const useAgeSelectionUx =
    useExperiment('search-use-age-selection-ux', {
      fallback: 'control',
    })?.toLowerCase() === 'enabled'

  const trackingData = useMemo(() => {
    return {
      pageType,
      metaTitle,
      region,
    }
  }, [pageType, metaTitle, region])

  const { loadCallback, loadState, loaded } = useLoadPage({
    loadMoreProducts,
    products,
  })

  const shouldInfiniteScrollUp = useRef(false)
  const shouldInfiniteScrollDown = useRef(false)

  const setShouldInfiniteScrollUp = (shouldInfiniteScroll: boolean) => {
    shouldInfiniteScrollUp.current = shouldInfiniteScroll
  }

  const setShouldInfiniteScrollDown = (shouldInfiniteScroll: boolean) => {
    shouldInfiniteScrollDown.current = shouldInfiniteScroll
  }

  const { offsetBorderApproaching, currentPageNumber } = usePagingOffset({
    loaded,
  })

  const endOfPageApproaching = useCallback(() => {
    if (shouldInfiniteScrollDown.current) {
      loadCallback(LoadMoreType.SCROLL)
    }
  }, [loadCallback, shouldInfiniteScrollDown])

  const topOfPageApproaching = useCallback(() => {
    if (shouldInfiniteScrollUp.current) {
      loadCallback(LoadMoreType.SCROLL_PREV)
    }
  }, [loadCallback, shouldInfiniteScrollUp])

  const handleProductClick = useCallback(
    (event: ProductClickedEvent) => {
      const { product } = event
      setProductPosition(product)
      onProductClicked(event)
    },
    [onProductClicked],
  )
  const dynamicTitle = buildSearchTitle(
    metaTitle,
    currentPageNumber,
    localiseText('find.page'),
  )

  return (
    <>
      <Head>
        <title key="page-dynamic-title">{dynamicTitle}</title>
        <meta name="title" content={dynamicTitle} key="dynamic-meta-title" />
      </Head>
      <Box>
        <InfiniteScroll
          loaded={loaded}
          products={products}
          totalCount={totalCount}
          loadCallback={loadCallback}
          loadState={loadState}
          setShouldInfiniteScrollUp={setShouldInfiniteScrollUp}
          setShouldInfiniteScrollDown={setShouldInfiniteScrollDown}
        >
          <ProductGrid
            products={products}
            totalNumberOfProducts={totalCount}
            onProductFirstClick={onProductFirstClick}
            onProductClicked={handleProductClick}
            onAddToBasket={onAddToBasket}
            onEndApproaching={endOfPageApproaching}
            onTopApproaching={topOfPageApproaching}
            onOffsetBorderApproaching={offsetBorderApproaching}
            isInternalUser={isInternalUser}
            departments={departments}
            trackingData={trackingData}
            groupCardProject={groupCardProject}
            promoTile={promoTile}
            featureEntryPointBanner={
              useAgeSelectionUx ? <AgeRangeFilterBanner /> : null
            }
          />
        </InfiniteScroll>
      </Box>
    </>
  )
}
